import axios from "axios";
import { getValidAccessToken } from "../../App/msal-auth/msalAuth";

export const api = axios.create({ baseURL: process.env.REACT_APP_EP_URL });

api.interceptors.request.use(
  async (config) => {
    /**
     * Authentication Token Handler
     *
     * This interceptor handles both Google and Microsoft authentication methods:
     * 1. First checks for Google authentication tokens (googleToken in localStorage)
     * 2. Falls back to Microsoft MSAL authentication for all other cases
     *
     * This prevents "No logged in account found" errors for Google users
     * while maintaining full compatibility with Microsoft authentication flows.
     */

    let token;

    // Check for Google authentication first
    const googleToken = localStorage.getItem("googleToken");
    if (googleToken) {
      try {
        const tokenData = JSON.parse(googleToken);
        // Google tokens can have either access_token or accessToken property
        token = tokenData.access_token || tokenData.accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          return config;
        }
      } catch (error) {
        // Invalid Google token - fall back to MSAL authentication
        console.warn(
          "Invalid Google token, falling back to MSAL authentication"
        );
      }
    }

    // Fall back to Microsoft MSAL authentication (existing behavior)
    try {      
      token = await getValidAccessToken();
    } catch (error) {      
      token = localStorage.getItem("MsAccessToken");
    }
 
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

export const generateGptCreditRequest = async (payload, slug, token) => {
  let config = {
    method: "post",
    url: `${process.env.REACT_APP_EP_URL}/api/credit/generate${slug}`,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify(payload),
  };

  const response = await axios.request(config);
  return response.data;
};

export const updateUserGptCredit = async (payload, slug, token) => {
  let config = {
    method: "put",
    url: `${process.env.REACT_APP_EP_URL}/api/user/update-user-credit${slug}`,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify(payload),
  };

  const response = await axios.request(config);
  return response.data;
};

export const getUserUsedStorage = async (slug, email, role) => {
  let config = {
    method: "get",
    url: `${process.env.REACT_APP_EP_URL}/api/storage/user${slug}&email=${email}&role=${role}`,
  };
  const response = await axios.request(config);
  return response.data;
};

export const getStorageAnalytics = async (slug, email) => {
  let config = {
    method: "get",
    url: `${process.env.REACT_APP_EP_URL}/api/storage/analytics${slug}&email=${email}`,
  };
  const response = await axios.request(config);
  return response.data;
};

export const getStorageDetails = async (slug, email, page, extension) => {
  let config = {
    method: "get",
    url: `${process.env.REACT_APP_EP_URL}/api/storage/details${slug}&email=${email}&page=${page}&extension=${extension}`,
  };
  const response = await axios.request(config);
  return response.data;
};
export const createChannel = async (slug, payload) => {
  let config = {
    method: "post",
    url: `${process.env.REACT_APP_EP_URL}/api/channel${slug}`,
    headers: {
      "Content-Type": "application/json",
    },
    data: JSON.stringify(payload),
  };

  const response = await axios.request(config);
  if (response.data.status !== "success") {
    throw new Error("Failed to create channel");
  }

  return response.data;
};

export const deleteChannel = async (slug, id) => {
  let config = {
    method: "delete",
    url: `${process.env.REACT_APP_EP_URL}/api/channel${slug}&id=${id}`,
    headers: {
      "Content-Type": "application/json",
    },
  };

  const response = await axios.request(config);
  if (response.data.status !== "success") {
    throw new Error("Failed to delete channel");
  }

  return response.data;
};

export const updateChannel = async (slug, id, payload) => {
  let config = {
    method: "put",
    url: `${process.env.REACT_APP_EP_URL}/api/channel${slug}&id=${id}`,
    headers: {
      "Content-Type": "application/json",
    },
    data: JSON.stringify(payload),
  };

  const response = await axios.request(config);
  if (response.data.status !== "success") {
    throw new Error("Failed to update channel");
  }

  return response.data;
};

export const getChannel = async (slug, type) => {
  let config = {
    method: "get",
    url: `${process.env.REACT_APP_EP_URL}/api/channel${slug}&preferType=${type}`,
    headers: {
      "Content-Type": "application/json",
    },
  };

  const response = await axios.request(config);
  if (response.data.status !== "success") {
    throw new Error("Failed to fetch channel");
  }

  return response.data;
};

export const compareChannelPassword = async (slug, payload) => {
  let config = {
    method: "put",
    url: `${process.env.REACT_APP_EP_URL}/api/channel/comparePassword${slug}`,
    headers: {
      "Content-Type": "application/json",
    },
    data: JSON.stringify(payload),
  };

  const response = await axios.request(config);
  return response.data;
};
